import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";
import { useOverrideAuthorName } from "../../../../../hooks/audit/useOverrideAuthorName";
import {
  type AnalyticsColumnResult,
  verdictMethodOf,
} from "../../../../../models/audit/analyticsColumnResult";
import { concludedByLine } from "../../lib/verdictOrigin";

/**
 * One detail, or nothing where the wire has no value to put in it.
 *
 * The judge columns are `NOT NULL` on a judged verdict, so a hole here is not a
 * state to design around — it is what stops a projection that stopped sending a
 * field from rendering the label with nothing after it.
 */
function detail(
  value: string | null,
  compose: (value: string) => string,
): string | null {
  if (value === null || value.trim() === "") {
    return null;
  }
  return compose(value);
}

interface VerdictProvenanceProps {
  result: AnalyticsColumnResult;
}

/**
 * Who concluded the verdict on screen, with the judge and run behind it on
 * hover.
 *
 * **The details are the only place in the UI the judge is named**, so the
 * tooltip is how they stay reachable. The effort is a detail of its own beside
 * the model: the same model at a different effort is a different judge.
 *
 * **A refusal says so in the details**, because it reads as Midship's on the
 * line just as a judgment does, and it wears the same badge as an inconclusive
 * judgment.
 *
 * **An override renders no judge fields.** The backend writes them null on an
 * override, and `verdictMethodOf` reports an override unjudged, so a
 * nonconforming row that carries them still shows none. The run the override
 * was made against stays in the details, so the evidence behind an overridden
 * cell stays reachable.
 */
export function VerdictProvenance({ result }: VerdictProvenanceProps) {
  const method = verdictMethodOf(result);
  const auditorName = useOverrideAuthorName(
    method === "human_override" ? result.userId : null,
  );
  const line = concludedByLine(method, auditorName);

  const details = [
    ...(method === "llm_judgment"
      ? [
          detail(result.judgeModelId, (model) => model),
          detail(result.judgeEffort, (effort) => `${effort} effort`),
          detail(result.judgePromptVersion, (version) => `prompt ${version}`),
        ]
      : []),
    method === "pipeline_refusal" ? "Refused before a judge ran" : null,
    detail(result.runManifestId, (runManifestId) => `run ${runManifestId}`),
  ].filter((part): part is string => part !== null);

  return (
    <div className="text-xs text-font-secondary">
      {details.length === 0 ? (
        line
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="cursor-help text-left">
              {line}
            </button>
          </TooltipTrigger>
          <TooltipContent>{details.join(" · ")}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
